import { useQuery } from "@tanstack/react-query";
import { X, BarChart3, Target, CheckCircle2, Clock, TrendingUp, BookOpen, Loader2, Flame, CalendarDays, Crosshair } from "lucide-react";
import { cn } from "@/lib/utils";

const BASE_URL = import.meta.env.BASE_URL?.replace(/\/$/, "") ?? "";

async function fetchWithAuth(path: string) {
  const token = localStorage.getItem("token");
  const res = await fetch(`${BASE_URL}${path}`, {
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

interface DailyActivity { date: string; sessions: number; questions: number }

interface UserStats {
  totalSessions: number;
  completedSessions: number;
  totalQuestions: number;
  totalCorrect: number;
  accuracy: number;
  lastActive: string | null;
  passRate: number;
  avgQuestionsPerSession: number;
  currentStreak: number;
  totalStudyDays: number;
  sessionModes: Array<{ mode: string; count: number }>;
  dailyActivity: DailyActivity[];
  byCategory: Array<{ category: string; totalAnswered: number; totalCorrect: number; accuracy: number }>;
  recentSessions: Array<{ id: number; mode: string; category: string | null; questions: number; accuracy: number; completedAt: string }>;
}

interface Props {
  userId: number;
  userName: string;
  userEmail: string;
  onClose: () => void;
}

const MODE_LABELS: Record<string, string> = {
  practice: "Practice", exam: "Exam", weakness: "Weakness", category: "Category",
};

const MODE_COLORS: Record<string, string> = {
  practice: "bg-primary/70", exam: "bg-blue-500/70", weakness: "bg-red-500/70", category: "bg-amber-500/70",
};

function AccBar({ pct }: { pct: number }) {
  const color = pct >= 80 ? "#22c55e" : pct >= 60 ? "#eab308" : "#ef4444";
  const bg = pct >= 80 ? "bg-green-500" : pct >= 60 ? "bg-yellow-500" : "bg-red-500";
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-secondary rounded-full overflow-hidden">
        <div className={cn("h-full rounded-full transition-all", bg)} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      <span className="text-xs font-mono font-bold w-10 text-right" style={{ color }}>
        {pct.toFixed(0)}%
      </span>
    </div>
  );
}

function MiniHeatmap({ data }: { data: DailyActivity[] }) {
  const max = Math.max(...data.map(d => d.sessions), 1);
  return (
    <div className="space-y-2">
      <div className="flex gap-0.5">
        {data.map(d => {
          const intensity = d.sessions === 0 ? 0 : Math.ceil((d.sessions / max) * 4);
          const bg = intensity === 0 ? "bg-secondary/40"
            : intensity === 1 ? "bg-primary/20"
            : intensity === 2 ? "bg-primary/40"
            : intensity === 3 ? "bg-primary/65"
            : "bg-primary";
          const dt = new Date(d.date + "T12:00:00");
          return (
            <div
              key={d.date}
              title={`${dt.toLocaleDateString("en-CA",{month:"short",day:"numeric"})}: ${d.sessions} sessions`}
              className={cn("flex-1 rounded-sm aspect-square min-w-0 cursor-default", bg)}
            />
          );
        })}
      </div>
      <div className="flex justify-between text-[9px] font-mono text-muted-foreground/40">
        <span>{new Date(data[0]?.date + "T12:00:00").toLocaleDateString("en-CA", { month: "short", day: "numeric" })}</span>
        <span>Today</span>
      </div>
    </div>
  );
}

export function UserStatsModal({ userId, userName, userEmail, onClose }: Props) {
  const { data: stats, isLoading, isError } = useQuery<UserStats>({
    queryKey: ["admin-user-stats", userId],
    queryFn: () => fetchWithAuth(`/api/admin/users/${userId}/stats`),
  });

  const totalModes = stats?.sessionModes.reduce((a, m) => a + m.count, 0) ?? 1;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-xl bg-card border border-border rounded-lg shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-secondary/30">
          <div>
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-primary" />
              <span className="font-bold font-mono text-sm tracking-wider text-foreground">USER STATISTICS</span>
            </div>
            <div className="text-xs text-muted-foreground font-mono mt-0.5">{userName} · {userEmail}</div>
          </div>
          <button onClick={onClose} className="text-muted-foreground/50 hover:text-foreground transition-colors p-1">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 max-h-[80vh] overflow-y-auto space-y-5">
          {isLoading && (
            <div className="flex items-center justify-center py-10 gap-2 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="font-mono text-sm">Loading stats...</span>
            </div>
          )}
          {isError && (
            <div className="text-center py-8 text-muted-foreground font-mono text-sm">Failed to load statistics.</div>
          )}

          {stats && (
            <>
              {/* Top summary cards — 4×2 */}
              <div className="grid grid-cols-4 gap-2">
                {[
                  { icon: BookOpen, label: "Sessions", value: stats.completedSessions, sub: `of ${stats.totalSessions}` },
                  { icon: Target, label: "Questions", value: stats.totalQuestions, sub: "answered" },
                  { icon: CheckCircle2, label: "Accuracy", value: `${stats.accuracy.toFixed(0)}%`, sub: "overall" },
                  { icon: TrendingUp, label: "Pass Rate", value: `${stats.passRate.toFixed(0)}%`, sub: "≥70% sessions" },
                  { icon: Flame, label: "Streak", value: `${stats.currentStreak}d`, sub: "current" },
                  { icon: CalendarDays, label: "Study Days", value: stats.totalStudyDays, sub: "total" },
                  { icon: Crosshair, label: "Avg Q/Sess", value: stats.avgQuestionsPerSession, sub: "questions" },
                  { icon: Clock, label: "Last Active", value: stats.lastActive
                    ? new Date(stats.lastActive).toLocaleDateString("en-CA", { month: "short", day: "numeric" })
                    : "—", sub: stats.lastActive ? new Date(stats.lastActive).getFullYear().toString() : "" },
                ].map(({ icon: Icon, label, value, sub }) => (
                  <div key={label} className="bg-secondary/30 border border-border rounded-md p-2.5 text-center">
                    <Icon className="w-3.5 h-3.5 text-primary mx-auto mb-1" />
                    <div className="text-base font-bold font-mono text-foreground leading-tight">{value}</div>
                    <div className="text-[9px] text-muted-foreground font-mono uppercase tracking-wider mt-0.5">{label}</div>
                    {sub && <div className="text-[9px] text-muted-foreground/50 font-mono">{sub}</div>}
                  </div>
                ))}
              </div>

              {/* 30-day activity heatmap */}
              {stats.dailyActivity && stats.dailyActivity.length > 0 && (
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground/60 mb-2">30-Day Activity</div>
                  <MiniHeatmap data={stats.dailyActivity} />
                </div>
              )}

              {/* Session modes */}
              {stats.sessionModes.length > 0 && (
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground/60 mb-2">Session Modes</div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {stats.sessionModes.map(m => (
                      <div key={m.mode} className="space-y-1">
                        <div className="flex justify-between text-[10px] font-mono text-muted-foreground">
                          <span>{MODE_LABELS[m.mode] ?? m.mode}</span>
                          <span className="text-foreground">{m.count} ({((m.count/totalModes)*100).toFixed(0)}%)</span>
                        </div>
                        <div className="h-1 bg-secondary rounded-full overflow-hidden">
                          <div className={cn("h-full rounded-full", MODE_COLORS[m.mode] ?? "bg-primary/60")} style={{ width: `${(m.count/totalModes)*100}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Category accuracy */}
              {stats.byCategory.length > 0 && (
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground/60 mb-2">Category Accuracy</div>
                  <div className="space-y-2">
                    {stats.byCategory.slice(0, 8).map(c => (
                      <div key={c.category} className="space-y-1">
                        <div className="flex justify-between text-[10px] font-mono">
                          <span className="text-foreground truncate max-w-[60%]">{c.category}</span>
                          <span className="text-muted-foreground/60">{c.totalAnswered} answered</span>
                        </div>
                        <AccBar pct={c.accuracy} />
                      </div>
                    ))}
                    {stats.byCategory.length > 8 && (
                      <div className="text-[10px] text-muted-foreground/40 font-mono text-center pt-1">
                        +{stats.byCategory.length - 8} more categories
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Recent sessions */}
              {stats.recentSessions.length > 0 && (
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground/60 mb-2">Recent Sessions</div>
                  <div className="space-y-1">
                    {stats.recentSessions.map(s => {
                      const accColor = s.accuracy >= 80 ? "text-green-400" : s.accuracy >= 60 ? "text-yellow-400" : "text-red-400";
                      return (
                        <div key={s.id} className="flex items-center gap-2 text-[10px] font-mono py-1 border-b border-border/20 last:border-0">
                          <span className="text-muted-foreground/50 w-16 shrink-0">
                            {new Date(s.completedAt).toLocaleDateString("en-CA", { month: "short", day: "numeric" })}
                          </span>
                          <span className="text-muted-foreground">{MODE_LABELS[s.mode] ?? s.mode}</span>
                          {s.category && <span className="text-muted-foreground/40 truncate flex-1">{s.category}</span>}
                          <span className="text-muted-foreground/50 ml-auto shrink-0">{s.questions}q</span>
                          <span className={cn("font-bold w-10 text-right shrink-0", accColor)}>{s.accuracy.toFixed(0)}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {stats.totalSessions === 0 && (
                <div className="text-center py-4 text-muted-foreground font-mono text-sm">
                  This user hasn't started any sessions yet.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
